import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import axios from 'axios';
import config from '@/config/ConfigProvider';
import eventBus from '@/foundations/services/eventBus';
import FileUploader from '@/components/epubguide/FileUploader.vue';
import type { Language } from '@ilteducation/content-ref-data-types';
import { getAllRefData } from '@/data-provider/refDataProvider';
import { Block, CsvConvertResult, CsvValidationResult, NewTask, Task } from '@ilteducation/the-publisher-types';
import { GetBlocksResponse, GetJobsResponse, Job } from '@ilteducation/studio-jobs-types';
import { generateUid } from '@ilteducation/uid';


export default /*#__PURE__*/_defineComponent({
  __name: 'CreateNewPublisherTaskForm',
  props: {
    tab: { default: 'studio' },
    contentId: null,
    language: null,
    pageCount: null,
    title: null
  },
  emits: ['created'],
  setup(__props: any, { emit }) {

const props = __props as { tab: string, contentId?: string, language?: string, pageCount?: number, title?: string }




const isCreating = ref(false);
const pageCount = ref(props.pageCount || 0);
const search = ref<string>('');

const emptyTask: NewTask = {
  name: props.title || '',
  contentId: props.contentId || '',
  contentType: 'Book',
  language: props.language || '',
  studioJobId: undefined,
  processWithAgent: true,
};

const task = ref<NewTask>({
  name: props.title || '',
  contentId: props.contentId || '',
  contentType: 'Book',
  language: props.language || '',
  // Default on: PDF tasks are created precisely so the agent can pre-process them.
  processWithAgent: true,
});

const blocks = ref<Block[]>([]);

const rules = {
  required: (value: unknown) => !!value || 'This field is required',
};

const availableLanguages = ref<Language[]>([]);
getAllRefData('languages').then((languages) => {
  availableLanguages.value = languages.sort((a: Language, b: Language) => a.language.localeCompare(b.language));
});

const allStatuses = [
  'created',
  'waiting-for-narration',
  'narrating',
  'waiting-for-proofing',
  'proofing',
  'waiting-for-correction',
  'correcting',
  'waiting-for-mastering',
  'mastering',
  'mastered',
  'completed',
  'archived',
  'deleted',
];
const selectedStatus = ref<string[]>(['completed']);
const jobs = ref<Job[]>([]);
const filteredJobs = computed(() => {
  return jobs.value.filter((job) => {
    if (selectedStatus.value.length > 0) {
      if (!selectedStatus.value.includes(job.status)) {
        return false;
      }
    }
    return true;
  });
});
const jobsLoading = ref(true);
axios.get<GetJobsResponse>(`${config.value('studioJobsUrl')}/jobs`).then((response) => {
  jobs.value = response.data.data;
  jobsLoading.value = false;
});

const uploadedCsvId = ref<string>();
const validationResult = ref<CsvValidationResult>();
const blockValidationInProgress = ref(false);

const uploadedPDF = ref<File>();

const fileChanged = async (file: File | undefined) => {
  if (!file) {
    return;
  }

  const text = await file.text();
  if (text.includes('�')) {
    eventBus.$emit('snackbarV2.open', {
      text: 'The CSV file appears to contain invalid characters. Please ensure the file is encoded in UTF-8 format.',
      color: 'error',
    });
    return;
  }

  if (!task.value.name || task.value.name.trim() === '') {
    task.value.name = file.name.replace('.csv', '');
  }

  validationResult.value = undefined;
  blockValidationInProgress.value = true;
  try {
    const csvUpload = await axios
      .post<{ id: string; url: string }>(`${config.value('thePublisherUrl')}/csv/upload-link`)
      .then(async (res) => res.data);
    uploadedCsvId.value = csvUpload.id;
    await fetch(csvUpload.url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'text/csv',
      },
    });
    validationResult.value = await axios
      .get<CsvValidationResult>(`${config.value('thePublisherUrl')}/csv/${uploadedCsvId.value}/validate`)
      .then((res) => res.data);
  } finally {
    blockValidationInProgress.value = false;
  }
};

watch(validationResult, async () => {
  if (validationResult.value?.valid && uploadedCsvId.value) {
    blockValidationInProgress.value = true;
    try {
      blocks.value = [];
      let done = false;
      let offset = 0;
      do {
        const res = await axios
          .get<CsvConvertResult>(`${config.value('thePublisherUrl')}/csv/${uploadedCsvId.value}/convert`, {
            params: { offset, limit: 500 },
          })
          .then((res) => res.data);
        blocks.value.push(...res.blocks);
        offset += res.blocks.length;
        done = blocks.value.length >= res.total;
      } while (!done);
      if (blocks.value && blocks.value.length > 0 && blocks.value[0].text) {
        task.value.language = Object.keys(blocks.value[0].text)[0];
      } else {
        task.value.language = '';
      }
    } finally {
      blockValidationInProgress.value = false;
    }
  }
});

const createBlocks = async (taskId: string) => {
  // Call createBlocks with 500 blocks at a time
  const chunkSize = 500;
  for (let i = 0; i < blocks.value.length; i += chunkSize) {
    const chunk = blocks.value.slice(i, i + chunkSize);
    await axios.post(`${config.value('thePublisherUrl')}/tasks/${taskId}/blocks`, {
      taskId,
      blocks: chunk,
    });
  }
};

const startPdfSourceExtraction = async (taskId: string) => {
  const { url: pdfUploadUrl } = await axios
    .get<{ url: string }>(`${config.value('thePublisherUrl')}/tasks/${taskId}/pdf/url/pdf?action=upload`)
    .then(async (res) => res.data);

  await fetch(pdfUploadUrl, {
    method: 'PUT',
    body: uploadedPDF.value,
    headers: {
      'Content-Type': 'application/pdf',
    },
  });
  await axios.post(`${config.value('thePublisherUrl')}/tasks/${taskId}/pdfextractor/run`);
};

const createTask = async () => {
  try {
    isCreating.value = true;

    if (tab.value === 'no-csv') {
      // Create blocks for the specified number of pages
      blocks.value = Array.from({ length: pageCount.value }, (_, i) => ({
        id: generateUid(),
        sequence: i,
        continuousPage: i + 1,
        blockType: 'h1',
        text: { [task.value.language]: `${i + 1}` },
      }));

      task.value.studioJobId = '__GENERATE__';
    }
    if (tab.value === 'csv') {
      task.value.studioJobId = '__GENERATE__';
    }

    const createdTask = await axios
      .post<Task>(`${config.value('thePublisherUrl')}/tasks`, task.value)
      .then((res) => res.data);

    if (tab.value === 'pdf') {
      await startPdfSourceExtraction(createdTask.id);
    } else {
      await createBlocks(createdTask.id);
      await axios.patch(`${config.value('thePublisherUrl')}/tasks/${createdTask.id}`, {
        status: 'studio-recording',
      });
    }

    Object.assign(task.value, emptyTask);
    blocks.value = [];
    selectedJob.value = [];
    emit('created');
    eventBus.$emit('snackbar.open', 'Task created successfully');
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      eventBus.$emit('snackbarV2.open', { text: `Error creating task ${error.response.data.message}`, color: 'error' });
    } else {
      const err = error as Error;
      eventBus.$emit('snackbarV2.open', { text: `Error creating task ${err.message ?? err}`, color: 'error' });
    }
  } finally {
    isCreating.value = false;
  }
};

const blockHeaders = [
  { value: 'sequence', text: 'Sequence' },
  { value: 'continuousPage', text: 'Page' },
  { value: 'blockType', text: 'Type' },
  { value: 'text', text: 'Text' },
];

const jobHeaders = [
  { value: 'id', text: 'Id' },
  { value: 'name', text: 'Name' },
  { value: 'status', text: 'Status' },
  { value: 'updatedAt', text: 'Updated' },
  { value: 'createdAt', text: 'Created' },
];

const tab = ref<string>(props.tab);
const selectedJob = ref<Job[]>([]);

function rowClicked(item: Job) {
  selectedJob.value = [item];
}

watch(tab, () => {
  blocks.value = [];
});

const loadingBlocksForJob = ref(false);

watch(selectedJob, async (value, oldValue) => {
  const job = value && value.length > 0 ? value[0] : undefined;
  const oldJob = oldValue && oldValue.length > 0 ? oldValue[0] : undefined;
  if (job) {
    if (job.id !== oldJob?.id) {
      task.value.name = job.name;
      task.value.language = job.language;
      task.value.studioJobId = job.id;

      blocks.value = [];
      blockValidationInProgress.value = true;
      try {
        let cursor = undefined;
        do {
          const resp: GetBlocksResponse = await axios
            .get<GetBlocksResponse>(`${config.value('studioJobsUrl')}/jobs/${job.id}/blocks`, {
              params: {
                cursor,
                limit: 500,
              },
            })
            .then((res) => res.data);
          blocks.value.push(...resp.data);
          cursor = resp.cursor;
        } while (cursor);
      } finally {
        blockValidationInProgress.value = false;
      }
    }
  } else {
    blocks.value = [];
  }
});

return { __sfc: true,props, emit, isCreating, pageCount, search, emptyTask, task, blocks, rules, availableLanguages, allStatuses, selectedStatus, jobs, filteredJobs, jobsLoading, uploadedCsvId, validationResult, blockValidationInProgress, uploadedPDF, fileChanged, createBlocks, startPdfSourceExtraction, createTask, blockHeaders, jobHeaders, tab, selectedJob, rowClicked, loadingBlocksForJob, FileUploader }
}

})